/**
 * FR-018.7 — the batch the group editor writes for a shared name (a `field_span` label)
 * (TENANT_CONFIGURATION_UI.md §5.1, §7).
 *
 * The batch is items only — never `supersedes`: the server's F-16 credits every live span row of
 * the label to the first item that overlaps it (`assign_rows` matches on system / kind / label, NOT
 * on target). So the batch is built from the `shared` row's `span_rows` — EVERY live confirmed span
 * row of the label, other fields' disjoint seasons included — never from the members list:
 *
 * - **Re-range** — one `field_span` item per span row that started before X, its own `valid_from`,
 *   ending at `min(valid_to, X - 1)`. A row ending before X is thereby restated unchanged (a sibling
 *   item would otherwise supersede it and move its seasons).
 * - Rows starting at or after X are replaced by the from-X arrangement:
 *   - **Keep shared** — one `field_span` item per ticked field from X; a field whose only rows
 *     after X start later keeps that later start (so a disjoint later row is restated as it is).
 *   - **Stop sharing** — one `field` item from X for the chosen field (§7: the narrowed span rows
 *     plus this row plan clean — no `overlaps_item`, no `shadowed_by_field_span`).
 * - The from-X items are open-ended unless EVERY span row is closed: then they end at the latest
 *   span year, so they never reach a later row (e.g. the `field` row an earlier stop-sharing wrote).
 *
 * The shape matches `backend/tests/unit/services/test_tenant_config_service.py::_stop_sharing_batch`.
 */
import type { SourceMappingItem, SourceSpanRow } from '../../../../services/sourcesService';

export type GroupMode = 'keep' | 'stop';

export interface GroupTarget {
    entityId: string;
    name: string;
}

export interface GroupItemsInput {
    sourceSystem: string | null;
    sourceLabel: string;
    /** The `shared` row's `span_rows`; `undefined` from an older backend. */
    spanRows: SourceSpanRow[] | undefined;
    /** Fields added in keep-shared mode (no span row at or after X yet). */
    added: GroupTarget[];
    /** `Ab Erntejahr` — the first harvest year of the new arrangement. */
    from: number;
    mode: GroupMode;
    /** Fields unticked (active targets or added ones). Keep mode only. */
    unticked: ReadonlySet<string>;
    /** The one field the name means from X. Stop mode only. */
    fieldId: string | null;
}

export type GroupItemsResult =
    /** `accountedIds`: every span row the batch restates or replaces — any other superseded id is a surprise. */
    | { status: 'ready'; items: SourceMappingItem[]; accountedIds: string[] }
    | { status: 'incomplete'; reason: 'field' | 'members' | 'unchanged' }
    | { status: 'unsupported' }
    | { status: 'reload' }
    /**
     * Batches the server would refuse, caught before a preview:
     * - `split`: stop sharing with span rows starting at or after X (an earlier change already re-split the
     *   name from `split`). No same-kind item can narrow them before X, so the `field` row would be
     *   `shadowed_by_field_span`; the first workable year is `split + 1`.
     * - `deleted`: a span row of a deleted (tombstoned) field would be restated → `target_not_live`.
     */
    | { status: 'blocked'; reason: 'split'; split: number; year: number }
    | { status: 'blocked'; reason: 'deleted' };

const startsBefore = (r: SourceSpanRow, year: number) => r.valid_from === null || r.valid_from < year;
const reaches = (r: SourceSpanRow, year: number) => r.valid_to === null || r.valid_to >= year;

/** Named targets with a span row at or after `from`, once each, in `span_rows` order. */
export function activeTargets(spanRows: SourceSpanRow[] | undefined, from: number): GroupTarget[] {
    const out: GroupTarget[] = [];
    for (const r of spanRows ?? []) {
        if (r.name && reaches(r, from) && !out.some((t) => t.entityId === r.target_ref)) {
            out.push({ entityId: r.target_ref, name: r.name });
        }
    }
    return out;
}

/** Named targets of every span row, once each (the stop-sharing choices). */
export function namedTargets(spanRows: SourceSpanRow[] | undefined): GroupTarget[] {
    return activeTargets(spanRows, Number.NEGATIVE_INFINITY);
}

/** `Ab Erntejahr` choices: earliest span start … next year, or the latest span end when every row is closed. */
export function groupYearBounds(spanRows: SourceSpanRow[] | undefined, currentYear: number): { min: number; max: number } {
    const rows = spanRows ?? [];
    const starts = rows.map((r) => r.valid_from).filter((v): v is number => v !== null);
    const min = starts.length ? Math.min(...starts) : currentYear - 6;
    const latest = latestEnd(rows);
    return { min, max: latest === null ? currentYear + 1 : Math.max(min, latest) };
}

/** The latest `valid_to` when every row is closed, else `null` (some row is open-ended). */
function latestEnd(rows: SourceSpanRow[]): number | null {
    if (!rows.length || rows.some((r) => r.valid_to === null)) return null;
    return Math.max(...rows.map((r) => r.valid_to as number));
}

export function buildGroupItems(input: GroupItemsInput): GroupItemsResult {
    const { sourceSystem, sourceLabel, spanRows, added, from, mode, unticked, fieldId } = input;
    if (!sourceSystem || !sourceLabel.trim()) return { status: 'unsupported' };
    if (!spanRows) return { status: 'reload' };
    if (spanRows.some((r) => r.name === null && startsBefore(r, from))) return { status: 'blocked', reason: 'deleted' };
    if (mode === 'stop') {
        const later = spanRows.map((r) => r.valid_from).filter((v): v is number => v !== null && v >= from);
        if (later.length) {
            const split = Math.min(...later);
            return { status: 'blocked', reason: 'split', split, year: split + 1 };
        }
    }

    const end = latestEnd(spanRows);
    const base = (kind: 'field_span' | 'field', target: string, vf: number | null, vt: number | null): SourceMappingItem => ({
        action: 'confirm',
        kind,
        relation: kind === 'field_span' ? 'spans' : 'is',
        source_system: sourceSystem,
        source_label: sourceLabel,
        target_ref: target,
        target_kind: 'entity',
        target_type: 'field',
        valid_from: vf,
        valid_to: vt,
    });

    let tail: SourceMappingItem[];
    if (mode === 'stop') {
        if (!fieldId) return { status: 'incomplete', reason: 'field' };
        tail = [base('field', fieldId, from, end)];
    } else {
        const active = activeTargets(spanRows, from);
        const candidates = [...active, ...added.filter((a) => !active.some((t) => t.entityId === a.entityId))];
        const sharing = candidates.filter((c) => !unticked.has(c.entityId));
        if (sharing.length < 2) return { status: 'incomplete', reason: 'members' };
        if (sharing.length === active.length && active.every((t) => !unticked.has(t.entityId))) {
            return { status: 'incomplete', reason: 'unchanged' };
        }
        tail = sharing.map((c) => {
            const own = spanRows.filter((r) => r.target_ref === c.entityId && reaches(r, from));
            const coversX = own.some((r) => !(r.valid_from !== null && r.valid_from > from));
            const later = own.map((r) => r.valid_from).filter((v): v is number => v !== null && v > from);
            const vf = !coversX && later.length ? Math.min(...later) : from;
            return base('field_span', c.entityId, vf, end);
        });
    }

    const rerange = spanRows
        .filter((r) => startsBefore(r, from))
        .map((r) => base('field_span', r.target_ref, r.valid_from, r.valid_to !== null ? Math.min(r.valid_to, from - 1) : from - 1));
    return { status: 'ready', items: [...rerange, ...tail], accountedIds: spanRows.map((r) => r.mapping_id) };
}
